import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { compressAll, type CompressedImage } from '@/pages/product-listing/utils/imageCompress';

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  thumbnail: string;
  images: string[];
  colors: { name: string; hex: string; images: string[]; image: string }[];
  status: string;
  created_at: string;
  stock: number;
  reject_reason?: string;
}

async function uploadImageToStorage(dataUrl: string, folder: string): Promise<string> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const ext = blob.type.split('/')[1] || 'jpg';
  const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  const { error } = await supabase.storage
    .from('product-images')
    .upload(fileName, blob, { contentType: blob.type, upsert: false });
  if (error) throw new Error(`Upload failed: ${error.message}`);
  const { data: urlData } = supabase.storage.from('product-images').getPublicUrl(fileName);
  return urlData.publicUrl;
}

// ── Image Update Modal ──
interface ImageUpdateModalProps {
  product: Product;
  onClose: () => void;
  onUpdated: (productId: string, newImages: string[], newThumbnail: string) => void;
}

function ImageUpdateModal({ product, onClose, onUpdated }: ImageUpdateModalProps) {
  const [existingImages, setExistingImages] = useState<string[]>(product.images ?? []);
  const [newImages, setNewImages] = useState<CompressedImage[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_TOTAL = 8;
  const remaining = MAX_TOTAL - existingImages.length - newImages.length;

  const processFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (remaining <= 0) return;
    const selected = Array.from(files).slice(0, remaining);
    const validFiles = selected.filter(f => f.type.startsWith('image/'));
    if (validFiles.length === 0) return;
    setCompressing(true);
    setError('');
    try {
      const compressed = await compressAll(validFiles);
      setNewImages(prev => [...prev, ...compressed]);
    } catch {
      setError('Image compress nahi hua. Dobara try karo.');
    } finally {
      setCompressing(false);
    }
  }, [remaining]);

  const removeExisting = (idx: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== idx));
  };

  const removeNew = (idx: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (existingImages.length + newImages.length === 0) {
      setError('Kam se kam 1 image rakhni chahiye');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const folder = `prod-${product.id}`;
      const uploadedNew: string[] = [];
      for (const img of newImages) {
        const url = await uploadImageToStorage(img.dataUrl, folder);
        uploadedNew.push(url);
      }
      const allImages = [...existingImages, ...uploadedNew];
      const newThumbnail = allImages[0] ?? product.thumbnail;

      const { error: dbError } = await supabase
        .from('products')
        .update({ images: allImages, thumbnail: newThumbnail, updated_at: new Date().toISOString() })
        .eq('id', product.id);

      if (dbError) throw new Error(dbError.message);

      setSuccess(true);
      setTimeout(() => {
        onUpdated(product.id, allImages, newThumbnail);
        onClose();
      }, 1200);
    } catch (err) {
      setError('Save nahi hua. Internet check karo aur dobara try karo.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl z-10">
          <div>
            <h3 className="text-gray-900 font-bold text-base">Images Update Karo</h3>
            <p className="text-gray-400 text-xs truncate max-w-[220px]">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
              <div>
                <p className="text-green-700 font-bold text-sm">Images update ho gayi!</p>
                <p className="text-green-600 text-xs">Changes save ho gaye hain</p>
              </div>
            </div>
          )}

          {/* Existing Images */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-700 font-bold text-sm">Current Images</p>
              <span className="text-xs text-gray-400">{existingImages.length} images</span>
            </div>
            {existingImages.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {existingImages.map((url, idx) => (
                  <div key={idx} className="relative group aspect-square">
                    <img
                      src={url}
                      alt={`Product ${idx + 1}`}
                      className={`w-full h-full object-cover rounded-xl border-2 ${idx === 0 ? 'border-orange-400' : 'border-gray-200'}`}
                    />
                    {idx === 0 && (
                      <div className="absolute top-1 left-1 bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md leading-none">THUMB</div>
                    )}
                    <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => removeExisting(idx)}
                        className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center text-white hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <i className="ri-image-line text-gray-300 text-2xl"></i>
                <p className="text-gray-400 text-xs mt-1">Koi current image nahi</p>
              </div>
            )}
          </div>

          {/* Add New Images */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-gray-700 font-bold text-sm">Naye Images Add Karo</p>
              <span className={`text-xs font-bold px-2 py-1 rounded-lg ${remaining <= 0 ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-500'}`}>
                {existingImages.length + newImages.length}/{MAX_TOTAL} max
              </span>
            </div>

            {newImages.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {newImages.map((img, idx) => (
                  <div key={idx} className="relative group aspect-square">
                    <img
                      src={img.dataUrl}
                      alt={`New ${idx + 1}`}
                      className="w-full h-full object-cover rounded-xl border-2 border-green-400"
                    />
                    <div className="absolute top-1 left-1 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md leading-none">NEW</div>
                    <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded-md leading-none">{img.sizeKB}KB</div>
                    <div className="absolute inset-0 bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => removeNew(idx)}
                        className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center text-white hover:bg-red-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {remaining > 0 && (
              <div
                onClick={() => !compressing && inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer.files); }}
                className={`w-full flex flex-col items-center justify-center gap-2 py-6 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                  dragOver ? 'border-orange-400 bg-orange-50' :
                  compressing ? 'border-gray-200 bg-gray-50 cursor-wait' :
                  'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50'
                }`}
              >
                {compressing ? (
                  <>
                    <div className="w-6 h-6 rounded-full border-2 border-orange-400 border-t-transparent animate-spin"></div>
                    <span className="text-xs text-orange-500 font-semibold">Compressing...</span>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                      <i className="ri-image-add-line text-orange-500 text-xl"></i>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-700">Photos Add Karo</p>
                      <p className="text-xs text-gray-400 mt-0.5">Tap karo ya drag &amp; drop · {remaining} aur add ho sakte hain</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {remaining <= 0 && (
              <div className="text-center py-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-amber-600 text-xs font-semibold">Maximum {MAX_TOTAL} images limit reach ho gayi</p>
                <p className="text-amber-500 text-[10px] mt-0.5">Kuch purani images delete karo naye add karne ke liye</p>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => processFiles(e.target.files)}
              onClick={e => { (e.target as HTMLInputElement).value = ''; }}
            />
          </div>

          {/* Compression info */}
          {newImages.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2 flex items-center gap-2">
              <i className="ri-image-edit-line text-green-500 text-sm shrink-0"></i>
              <p className="text-green-700 text-xs">
                <span className="font-semibold">{newImages.length} naye images</span> auto-compressed —{' '}
                {newImages.reduce((s, i) => s + i.originalSizeKB, 0)}KB → {newImages.reduce((s, i) => s + i.sizeKB, 0)}KB
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm shrink-0"></i>
              <p className="text-red-600 text-xs">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={uploading || success || (existingImages.length + newImages.length === 0)}
              className="flex-1 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <><i className="ri-loader-4-line animate-spin"></i>Saving...</>
              ) : success ? (
                <><i className="ri-check-line"></i>Saved!</>
              ) : (
                <><i className="ri-save-line"></i>Save Changes</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──
export default function MyListedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');

  useEffect(() => {
    loadMyProducts();
  }, []);

  const loadMyProducts = async () => {
    setLoading(true);
    setError('');
    try {
      let phone: string | null = null;
      let email: string | null = null;
      try {
        const auth = localStorage.getItem('clothybazar_customer_auth');
        if (auth) {
          const user = JSON.parse(auth);
          phone = user?.phone || null;
          email = user?.email || null;
        }
      } catch { /* ignore */ }

      if (!phone && !email) {
        setError('Login karo apne listed products dekhne ke liye');
        setLoading(false);
        return;
      }

      let query = supabase
        .from('products')
        .select('id, name, category, price, thumbnail, images, colors, status, created_at, stock, reject_reason')
        .order('created_at', { ascending: false });

      if (phone) {
        query = query.eq('listed_by_phone', phone);
      } else if (email) {
        query = query.eq('listed_by_email', email);
      }

      const { data, error: dbError } = await query;
      if (dbError) throw new Error(dbError.message);
      setProducts(data ?? []);
    } catch {
      setError('Products load nahi hue. Dobara try karo.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpdated = (productId: string, newImages: string[], newThumbnail: string) => {
    setProducts(prev =>
      prev.map(p => p.id === productId ? { ...p, images: newImages, thumbnail: newThumbnail } : p)
    );
  };

  const filtered = filterStatus === 'all' ? products : products.filter(p => p.status === filterStatus);

  const statusConfig = {
    pending:  { label: 'Pending',  bg: 'bg-amber-100',  text: 'text-amber-700',  icon: 'ri-time-line' },
    approved: { label: 'Live',     bg: 'bg-green-100',  text: 'text-green-700',  icon: 'ri-checkbox-circle-line' },
    rejected: { label: 'Rejected', bg: 'bg-red-100',    text: 'text-red-600',    icon: 'ri-close-circle-line' },
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-orange-400 border-t-transparent animate-spin"></div>
        <p className="text-gray-400 text-sm">Products load ho rahe hain...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
        <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-gray-400 text-3xl"></i>
        </div>
        <div>
          <p className="text-gray-700 font-semibold text-sm">{error}</p>
          <p className="text-gray-400 text-xs mt-1">Profile se login karo phir wapas aao</p>
        </div>
        <button
          onClick={loadMyProducts}
          className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold cursor-pointer hover:bg-gray-800 transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1.5"></i>Retry
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
        <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center">
          <i className="ri-store-2-line text-orange-400 text-3xl"></i>
        </div>
        <div>
          <p className="text-gray-700 font-bold text-base">Koi product listed nahi</p>
          <p className="text-gray-400 text-sm mt-1">Apna pehla product list karo!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Total', count: products.length, color: 'bg-gray-100 text-gray-700' },
          { label: 'Live', count: products.filter(p => p.status === 'approved').length, color: 'bg-green-100 text-green-700' },
          { label: 'Pending', count: products.filter(p => p.status === 'pending').length, color: 'bg-amber-100 text-amber-700' },
        ].map(stat => (
          <div key={stat.label} className={`${stat.color} rounded-xl p-3 text-center`}>
            <p className="text-xl font-bold leading-none">{stat.count}</p>
            <p className="text-xs font-semibold mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
        {(['all', 'pending', 'approved', 'rejected'] as const).map(status => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${
              filterStatus === status ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {status === 'all' ? 'All' : status === 'approved' ? 'Live' : status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Product List */}
      <div className="space-y-3">
        {filtered.map(product => {
          const cfg = statusConfig[product.status as keyof typeof statusConfig] ?? statusConfig.pending;
          const totalImages = (product.images?.length ?? 0) + (product.colors?.reduce((s, c) => s + (c.images?.length ?? 0), 0) ?? 0);
          return (
            <div key={product.id} className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex gap-3">
                {/* Thumbnail */}
                <div className="w-16 h-16 rounded-xl overflow-hidden border border-gray-200 shrink-0 bg-gray-100">
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-gray-300 text-xl"></i>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-gray-900 font-bold text-sm leading-tight truncate">{product.name}</p>
                    <span className={`${cfg.bg} ${cfg.text} text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0 whitespace-nowrap`}>
                      <i className={`${cfg.icon} text-[10px]`}></i>
                      {cfg.label}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs mt-0.5">{product.category} · ₹{product.price}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-image-line text-[11px]"></i>
                      {totalImages} image{totalImages !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-stack-line text-[11px]"></i>
                      {product.stock} in stock
                    </span>
                  </div>
                  {product.status === 'rejected' && product.reject_reason && (
                    <div className="mt-2 bg-red-50 border border-red-100 rounded-lg px-2 py-1.5">
                      <p className="text-red-600 text-[10px] font-semibold flex items-center gap-1">
                        <i className="ri-information-line"></i>Reject reason:
                      </p>
                      <p className="text-red-500 text-[10px] mt-0.5">{product.reject_reason}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setEditingProduct(product)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-orange-50 text-orange-600 border border-orange-200 text-xs font-semibold hover:bg-orange-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-image-edit-line text-sm"></i>
                  Update Images
                </button>
                <button
                  onClick={() => setEditingProduct(product)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 text-gray-600 border border-gray-200 text-xs font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-sm"></i>
                  Add More
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">
          Is category mein koi product nahi
        </div>
      )}

      {/* Image Update Modal */}
      {editingProduct && (
        <ImageUpdateModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onUpdated={handleImageUpdated}
        />
      )}
    </div>
  );
}
